import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FEEDBACK_MESSAGE_TYPE } from "../../../configs/feedbackMessages";
import { CERTIFICATION_TYPES } from "../../../configs/constants/DocumentTypes";
import { POLICE_EXTRACT_TYPES } from "../../../configs/constants/PoliceExtractTypes";
import { Certification, Documents, OtherDocument } from "../../../models/Consultant";
import { appTheme } from "../../../theme/Theme";
import AppIcon from "../../Core/AppIcon/AppIcon";
import { CreateButton } from "../../Core/Button/Button";
import Flex from "../../Layout/Flex";
import TableLayout from "../../Layout/TableLayout";
import EmptyState from "../EmptyState/EmptyState";
import SimpleConfirmationModal from "../SimpleConfirmationModal/SimpleConfirmationModal";
import DocumentTypeComponent from "./DocumentTypeComponent";
import DocumentUploadModal from "./DocumentUploadModal";
import Checkbox from "../../Core/Checkbox/Checkbox";
import { toast } from "../../../utils/toast";

type Props = {
	documents?: Documents;
	updateDocuments: Function;
	isCandidate: boolean;
};

const DocumentTab: React.FC<Props> = ({ documents, updateDocuments, isCandidate }) => {
	const { t } = useTranslation();

	const [uploadModalVisible, setUploadModalVisible] = useState<boolean>(false);
	const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
	const [isCertification, setIsCertification] = useState<boolean>(false);
	const [selectedDocumentName, setSelectedDocumentName] = useState<string>("");
	const [deleteDocumentIndex, setDeleteDocumentIndex] = useState<number>();
	const [loading, setLoading] = useState<boolean>(false);

	const deleteCertificate = async (i: number) => {
		setLoading(true);

		const result = await updateDocuments({
			...documents,
			certifications: deleteDocuments(i, documents?.certifications) as Certification[],
		});

		setLoading(false);
		handleDeleteSuccess(result);
	};

	const viewForConsultantCertificate = async (i: number, isChecked: boolean) => {
		setLoading(true);

		if (documents?.certifications) {
			const result = await updateDocuments({
				...documents,
				certifications: forConsultantsDocuments(
					i,
					documents.certifications,
					isChecked
				) as Certification[],
			});

			setLoading(false);
		}
	};

	const viewForConsultantOtherDocuments = async (i: number, isChecked: boolean) => {
		setLoading(true);

		if (documents?.otherDocuments) {
			const result = await updateDocuments({
				...documents,
				otherDocuments: forConsultantsOtherDocuments(
					i,
					documents.otherDocuments,
					isChecked
				) as OtherDocument[],
			});

			setLoading(false);
		}
	};

	const deleteOtherDocument = async (i: number) => {
		setLoading(true);

		const result = await updateDocuments({
			...documents,
			otherDocuments: deleteDocuments(i, documents?.otherDocuments) as OtherDocument[],
		});

		setLoading(false);
		handleDeleteSuccess(result);
	};

	const deleteDocuments = (
		i: number,
		documents?: Certification[] | OtherDocument[]
	): Certification[] | OtherDocument[] => {
		documents = documents || [];

		if (documents.length === 1) documents = [];
		else documents.splice(i, 1);

		return documents;
	};

	const forConsultantsDocuments = (
		i: number,
		documents: Certification[],
		isChecked: boolean
	): Certification[] => {
		documents = documents || [];
		if (documents.length === 1) {
			documents[0].visibleToConsultant = isChecked;
		} else {
			let tempConsultant = documents[i];
			tempConsultant.visibleToConsultant = isChecked;
			documents.splice(i, 1, tempConsultant);
		}
		return documents;
	};

	const forConsultantsOtherDocuments = (
		i: number,
		documents: OtherDocument[],
		isChecked: boolean
	): OtherDocument[] => {
		documents = documents || [];
		if (documents.length === 1) {
			documents[0].visibleToConsultant = isChecked;
		} else {
			let tempConsultant = documents[i];
			tempConsultant.visibleToConsultant = isChecked;
			documents.splice(i, 1, tempConsultant);
		}

		return documents;
	};

	const handleUploadDocuments = useCallback(() => {
		setUploadModalVisible(true);
	}, [setUploadModalVisible]);

	const handleCancelUploadModal = useCallback(() => {
		setUploadModalVisible(false);
	}, [setUploadModalVisible]);

	const handleDeleteModal = useCallback(
		(i: number, name: string, certification: boolean) => {
			setDeleteModalVisible(true);
			setSelectedDocumentName(name);
			setDeleteDocumentIndex(i);
			setIsCertification(certification);
		},
		[setDeleteModalVisible, setIsCertification, setDeleteDocumentIndex, setSelectedDocumentName]
	);

	const handleDeleteOncancel = () => {
		setDeleteModalVisible(false);
		setSelectedDocumentName("");
		setDeleteDocumentIndex(undefined);
		setIsCertification(false);
	};

	const handleDeleteDocumentsConfirm = () => {
		if (deleteDocumentIndex !== undefined)
			if (isCertification) deleteCertificate(deleteDocumentIndex);
			else deleteOtherDocument(deleteDocumentIndex);
	};

	const handleViewForConsultant = (
		index: number,
		documentName: string,
		isCertificate: boolean,
		isChecked: boolean
	) => {
		if (index !== undefined) {
			if (isCertificate) {
				viewForConsultantCertificate(index, isChecked);
			} else {
				viewForConsultantOtherDocuments(index, isChecked);
			}
		}
	};

	const handleDeleteSuccess = useCallback((result: any) => {
		if (result) toast.success(FEEDBACK_MESSAGE_TYPE.CHANGES_SAVE_SUCCESSFULLY);
		else toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);

		handleDeleteOncancel();
	}, []);

	const getPoliceExtractTypeLabel = (policeExtractTypeKey?: string): string => {
		if (!policeExtractTypeKey) return "";
		const match = POLICE_EXTRACT_TYPES.find(({ key }) => key === policeExtractTypeKey);
		return match ? t(match.labelKey as any) : "";
	};

	const tableRow = (
		i: number,
		url: string,
		name: string,
		type: string,
		mimeType: string,
		isCertification: boolean,
		visibleToConsultant: boolean,
		policeExtractTypeKey?: string
	) => {
		return (
			<tr key={`${+isCertification + i}`}>
				<td>
					<a href={url} target="_blank" rel="noopener noreferrer">
						{t(name as any)}
					</a>
				</td>
				<td>{t(type as any)}</td>
				<td>{t(`mimeTypes.${mimeType}` as any)}</td>
				<td>
					{type === CERTIFICATION_TYPES.POLICE_RECORD_EXTRACTS
						? getPoliceExtractTypeLabel(policeExtractTypeKey)
						: ""}
				</td>
				<td>
					<Checkbox
						label={""}
						isChecked={visibleToConsultant}
						onChange={(e: any) =>
							handleViewForConsultant(i, name, isCertification, e.target.checked)
						}
					/>
				</td>
				<td>
					<AppIcon icon={"cross"} onClick={() => handleDeleteModal(i, name, isCertification)} />
				</td>
			</tr>
		);
	};

	return (
		<DocumentTableWrapper>
			<Flex margin="1rem 0 0 0" justifyContent="flex-end">
				<CreateButton onClick={handleUploadDocuments}>{t("UPLOAD_DOCUMENT")}</CreateButton>
			</Flex>

			{documents?.certifications?.length || documents?.otherDocuments?.length ? (
				<TableLayout>
					<table>
						<thead>
							<tr>
								<th>{t("DOCUMENT_NAME")}</th>
								<th>{t("DOCUMENT_TYPE")}</th>
								<th>{t("FILE_FORMAT")}</th>
								<th>{t("POLICE_EXTRACT_TYPE")}</th>
								<th>{t("VIEW_IN_CONSULTANT_APP")}</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{(documents.certifications || []).map(
								({ name, type, mimeType, url, visibleToConsultant, policeExtractTypeKey }, i) =>
									tableRow(
										i,
										url,
										name,
										type,
										mimeType,
										true,
										visibleToConsultant,
										policeExtractTypeKey
									)
							)}
							{(documents.otherDocuments || []).map(
								({ name, type, mimeType, url, visibleToConsultant }, i) =>
									tableRow(i, url, name, type, mimeType, false, visibleToConsultant)
							)}
						</tbody>
					</table>
				</TableLayout>
			) : (
				<EmptyState margin="2rem 0" label="NO_DOCUMENT" />
			)}

			<DocumentTypeComponent
				documents={documents}
				updateDocuments={updateDocuments}
				isCandidate={isCandidate}
			/>

			{uploadModalVisible && (
				<DocumentUploadModal
					onCancelModal={handleCancelUploadModal}
					documents={documents}
					updateDocuments={updateDocuments}
					isCandidate={isCandidate}
				/>
			)}
			{deleteModalVisible && (
				<SimpleConfirmationModal
					title={t("DELETE_DOCUMENT")}
					bodyText={`${t("DELETE_DOCUMENT_TEXT")} ${selectedDocumentName}?`}
					onCancel={handleDeleteOncancel}
					onConfirmation={handleDeleteDocumentsConfirm}
					isLoading={loading}
				/>
			)}
		</DocumentTableWrapper>
	);
};

export default DocumentTab;

const DocumentTableWrapper = styled.div`
	a {
		text-decoration: underline;
		color: ${appTheme.colors.COLOR_ACTION_BLUE};
	}
`;
